import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState } from "react";
import { fmtIST } from "@/shared/lib/time";
import { useParams, Link } from "react-router-dom";
import api, { fmtErr, API } from "@/shared/lib/api";
import {
  ArrowLeft, Printer, Download, Clock, CheckCircle2, Circle,
  Users, Palette, Ruler, Sparkles, Truck, Home, Timer, TrendingUp,
  AlertTriangle, FileSpreadsheet, Activity, Loader2, Award,
} from "lucide-react";
import { toast } from "sonner";

/**
 * ProjectReport — Project Efficiency Report (Admin-only)
 * Msg 1063: cleaner, more realistic metrics with SLA evaluation per stage and
 * an admin-only "Download Excel" action.
 */
const SLA_HRS = {
  design_to_client_approval_hrs:  72,
  client_approval_to_site_hrs:    48,
  site_to_sample_hrs:             72,
  sample_to_pre_press_hrs:        48,
  pre_press_to_files_hrs:         24,
  files_to_production_hrs:        120,
  production_to_delivery_hrs:     48,
  delivery_to_installation_hrs:   72,
};

const DURATION_LABELS = {
  design_to_client_approval_hrs: "Client response",
  client_approval_to_site_hrs:   "Site scheduling",
  site_to_sample_hrs:            "Mock sample",
  sample_to_pre_press_hrs:       "Pre-press",
  pre_press_to_files_hrs:        "File prep",
  files_to_production_hrs:       "Production",
  production_to_delivery_hrs:    "Delivery",
  delivery_to_installation_hrs:  "Installation",
};

export default function ProjectReport() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [eff, setEff] = useState(null);        // {stats, narrative, narrative_generated_at}
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${code}/report`).then((r) => setData(r.data))
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail) || "Failed"));
    api.get(`/projects/${code}/ai-efficiency/latest`).then((r) => setEff(r.data)).catch(() => {});
  }, [code]);

  async function runAiAnalysis() {
    setAiBusy(true);
    try {
      const { data: start } = await api.post(`/projects/${code}/ai-efficiency`);
      const jobId = start.job_id;
      const t0 = Date.now();
      while (Date.now() - t0 < 180000) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: job } = await api.get(`/projects/${code}/ai-efficiency/${jobId}`);
        if (job.status === "done") {
          setEff({ stats: job.stats, narrative: job.narrative });
          toast.success("AI analysis ready");
          return;
        }
        if (job.status === "error") { toast.error(job.error || "Analysis failed"); return; }
      }
      toast.error("Analysis timed out. Please try again.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Analysis failed");
    } finally { setAiBusy(false); }
  }

  if (!data) return <div className="p-8 text-zinc-500">Loading report…</div>;
  const { project, lines } = data;

  // ─── Honest metrics (Msg 1063) ───────────────────────────────────────
  const completed = lines.filter((l) => l.current_stage === "installed").length;
  const inProgress = lines.length - completed;

  // SLA-based on-time rate (out of completed stage transitions across all lines)
  let okCount = 0, breachCount = 0;
  const stageBreachTally = {};
  for (const ln of lines) {
    for (const [k, hrs] of Object.entries(ln.durations_hrs || {})) {
      if (hrs == null) continue;
      if (hrs <= SLA_HRS[k]) okCount += 1;
      else { breachCount += 1; stageBreachTally[k] = (stageBreachTally[k] || 0) + 1; }
    }
  }
  const totalEvents = okCount + breachCount;
  const onTimePct = totalEvents > 0 ? Math.round((okCount / totalEvents) * 100) : null;

  // Median total elapsed for completed lines (quick "throughput" feel)
  const elapsedDone = lines
    .filter((l) => l.current_stage === "installed" && l.total_elapsed_hrs != null)
    .map((l) => l.total_elapsed_hrs).sort((a, b) => a - b);
  const median = elapsedDone.length ? elapsedDone[Math.floor(elapsedDone.length / 2)] : null;

  // Bottleneck = stage transition with the most SLA breaches
  let bottleneck = null;
  for (const [k, n] of Object.entries(stageBreachTally)) {
    if (!bottleneck || n > bottleneck.n) bottleneck = { k, label: DURATION_LABELS[k], n };
  }

  async function downloadXlsx() {
    try {
      const tok = localStorage.getItem("token");
      const r = await fetch(`${API}/projects/${code}/report/download`,
                            { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${code}-efficiency-report.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e.message === "403" ? "Admin only" : "Download failed");
    }
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-card { break-inside: avoid; box-shadow: none !important; border-color: #e4e4e7 !important; }
          .app-sidebar { display: none !important; }
          body { background: white !important; }
        }
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-in { animation: fadeSlide .45s ease both; }
      `}</style>

      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="project-report">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3 no-print">
          <Link to={`/projects/${code}`}
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900">
            <ArrowLeft className="w-4 h-4" /> Back to project
          </Link>
          <div className="flex gap-2">
            <button onClick={() => window.print()}
                    className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-all"
                    data-testid="report-print-btn">
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
            <button onClick={downloadXlsx}
                    className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                    data-testid="report-xlsx-btn">
              <FileSpreadsheet className="w-4 h-4" /> Download Excel
            </button>
            <button onClick={runAiAnalysis} disabled={aiBusy}
                    className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-60"
                    data-testid="report-ai-btn">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiBusy ? "Analysing…" : "Analyse with AI"}
            </button>
          </div>
        </div>

        {/* Report hero */}
        <div className="rounded-3xl bg-gradient-to-br from-zinc-900 to-zinc-700 text-white p-6 sm:p-8 mb-8 fade-in print-card">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300">
            Project Efficiency Report · MIPL Global
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            {project.name}
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-zinc-300">
            <span><span className="font-mono text-amber-300"><ProjRef code={project.project_code} /></span></span>
            <span>Client: {project.client_name}</span>
            {project.location && <span>Site: {project.location}</span>}
          </div>

          {/* Honest KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <Kpi label="Line items" value={lines.length} hint={`${completed} done · ${inProgress} open`} />
            <Kpi label="On-time stages" value={onTimePct != null ? `${onTimePct}%` : "—"}
                 tone={onTimePct == null ? null : onTimePct >= 80 ? "emerald" : onTimePct >= 60 ? "amber" : "rose"}
                 hint={`${okCount} on-time · ${breachCount} over SLA`} />
            <Kpi label="Median cycle (completed)" value={median != null ? `${median}h` : "—"}
                 tone="blue" hint={`${elapsedDone.length} line${elapsedDone.length === 1 ? "" : "s"} installed`} />
            <Kpi label="Bottleneck" value={bottleneck ? bottleneck.label : "—"}
                 tone={bottleneck ? "rose" : null}
                 hint={bottleneck ? `${bottleneck.n} SLA breach${bottleneck.n === 1 ? "" : "es"}` : "no breaches yet"} />
          </div>
        </div>

        {/* AI Efficiency Analysis */}
        {eff && <AiEfficiencyPanel eff={eff} busy={aiBusy} onRun={runAiAnalysis} />}

        {/* Per-line timeline */}
        <div className="space-y-6">
          {lines.map((l, idx) => (
            <LineReportCard key={l.boq_id} line={l} idx={idx} />
          ))}
        </div>

        <div className="text-[11px] font-mono text-zinc-400 text-center mt-10">
          Generated {(data.generated_at || "").slice(0, 19).replace("T", " ")} UTC
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, tone, hint }) {
  const tones = {
    emerald: "text-emerald-300",
    amber:   "text-amber-300",
    blue:    "text-blue-300",
    rose:    "text-rose-300",
  };
  return (
    <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</div>
      <div className={`font-display text-2xl sm:text-3xl font-bold mt-1 leading-tight ${tones[tone] || "text-white"}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

function AiEfficiencyPanel({ eff, busy, onRun }) {
  const stats = eff.stats || {};
  const t = stats.totals || {};
  const designers = stats.designers || [];
  const me = stats.most_efficient_designer;
  const reworkLines = (stats.lines || []).filter((l) => l.rework_rounds > 0).slice(0, 6);
  return (
    <div className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-6 sm:p-7 mb-8 print-card" data-testid="ai-efficiency-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight">AI Efficiency Analysis</h2>
            <div className="text-xs text-zinc-500">Designer performance, rework rounds & bottlenecks</div>
          </div>
        </div>
        <button onClick={onRun} disabled={busy}
                className="no-print inline-flex items-center gap-2 px-4 h-9 rounded-full bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-60"
                data-testid="ai-efficiency-rerun">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Analysing…" : eff.narrative ? "Re-analyse" : "Analyse with AI"}
        </button>
      </div>

      {/* Stat chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatChip label="Design versions" value={t.design_versions ?? 0} />
        <StatChip label="Approved" value={t.approved ?? 0} tone="emerald" />
        <StatChip label="Rework rounds" value={t.rework_rounds ?? 0} tone={t.rework_rounds ? "rose" : null} />
        <StatChip label="BOQ lines" value={t.boq_lines ?? 0} />
      </div>

      {me && (
        <div className="flex items-center gap-2 mb-5 text-sm">
          <Award className="w-4 h-4 text-emerald-600" />
          <span>Most efficient designer: <b className="text-emerald-700">{me}</b></span>
        </div>
      )}

      {/* Designer table */}
      {designers.length > 0 && (
        <div className="overflow-x-auto mb-5 rounded-xl border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-wider">
              <tr>
                <th className="text-left p-2.5">Designer</th>
                <th className="text-center p-2.5">Versions</th>
                <th className="text-center p-2.5">Approved</th>
                <th className="text-center p-2.5">Rework</th>
                <th className="text-center p-2.5">First-pass %</th>
              </tr>
            </thead>
            <tbody>
              {designers.map((d) => (
                <tr key={d.designer} className="border-t border-zinc-100">
                  <td className="p-2.5 font-medium">
                    {d.designer}
                    {d.designer === me && <span className="ml-2 text-[10px] text-emerald-600 font-mono uppercase">★ best</span>}
                  </td>
                  <td className="p-2.5 text-center">{d.versions}</td>
                  <td className="p-2.5 text-center text-emerald-700 font-medium">{d.approved}</td>
                  <td className={`p-2.5 text-center font-medium ${d.rework > 0 ? "text-rose-600" : "text-zinc-400"}`}>{d.rework}</td>
                  <td className="p-2.5 text-center">{d.first_pass_approval_pct != null ? `${d.first_pass_approval_pct}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reworkLines.length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Lines needing most iterations</div>
          <div className="flex flex-wrap gap-2">
            {reworkLines.map((l) => (
              <span key={l.boq_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-xs">
                {l.line} · {l.rework_rounds} rework · {l.design_versions} versions
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AI narrative */}
      {eff.narrative ? (
        <div className="rounded-xl bg-white border border-zinc-200 p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-violet-600 mb-2">AI Verdict</div>
          <div className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed" data-testid="ai-efficiency-narrative">{eff.narrative}</div>
        </div>
      ) : (
        <div className="text-sm text-zinc-500 italic">Click "Analyse with AI" for a written verdict on designer efficiency, rework causes and bottlenecks.</div>
      )}
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const tones = { emerald: "text-emerald-600", rose: "text-rose-600" };
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-3 text-center">
      <div className={`font-display text-2xl font-bold ${tones[tone] || "text-zinc-900"}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

const STAGES = [
  { key: "design",          label: "Design Uploaded",   at: "design_done_at",       by: "design_by",       icon: Palette },
  { key: "client_approval", label: "Client Approved",   at: "client_approved_at",   by: "client_approver", icon: CheckCircle2 },
  { key: "site",            label: "Site Measurements", at: "site_measured_at",     by: "site_by",         icon: Ruler },
  { key: "sample",          label: "Mock Sample Done",  at: "sample_done_at",       by: "sample_by",       icon: Sparkles },
  { key: "pre_press",       label: "Pre-Press Done",    at: "pre_press_done_at",    by: "pre_press_by",    icon: Activity },
  { key: "files",           label: "Files Ready",       at: "files_ready_at",       by: "files_by",        icon: Activity },
  { key: "production",      label: "Production Done",   at: "production_done_at",   by: "production_by",   icon: TrendingUp },
  { key: "delivery",        label: "Delivered",         at: "delivery_done_at",     by: "delivery_by",     icon: Truck },
  { key: "installation",    label: "Installed On-site", at: "installation_done_at", by: "installation_by", icon: Home },
];

function rateLine(line) {
  let breaches = 0, counted = 0;
  for (const [k, hrs] of Object.entries(line.durations_hrs || {})) {
    if (hrs == null) continue;
    counted += 1;
    if (hrs > SLA_HRS[k]) breaches += 1;
  }
  if (counted === 0) return { label: "Not started", cls: "bg-zinc-100 text-zinc-600 border-zinc-200", Icon: Circle };
  if (breaches === 0) return { label: "On-track", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: CheckCircle2 };
  if (breaches === 1) return { label: "Minor delay", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: AlertTriangle };
  return { label: `${breaches} SLA breaches`, cls: "bg-rose-100 text-rose-700 border-rose-300", Icon: AlertTriangle };
}

function LineReportCard({ line, idx }) {
  const rating = rateLine(line);
  const RatingIcon = rating.Icon;
  return (
    <div className="card-flat p-5 sm:p-6 fade-in print-card"
         style={{ animationDelay: `${idx * 80}ms` }}
         data-testid={`report-line-${line.boq_id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          {line.wall_no && (
            <div className="font-mono text-[11px] text-orange-600 font-medium mb-0.5">
              {line.wall_no}
            </div>
          )}
          <h3 className="font-display text-xl font-bold tracking-tight">
            {line.material_name}
          </h3>
          <div className="text-xs text-zinc-500 mt-0.5">
            {line.quantity} {line.unit}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <CurrentStagePill stage={line.current_stage} />
          <span className={`inline-flex items-center gap-1 px-3 h-7 rounded-full text-[11px] font-medium border ${rating.cls}`}
                data-testid={`report-rating-${line.boq_id}`}>
            <RatingIcon className="w-3 h-3" /> {rating.label}
          </span>
        </div>
      </div>

      {/* Team allocation */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {[
          ["Designer",   line.assigned_team.designer,    "bg-blue-50 text-blue-700 border-blue-200"],
          ["Prepress",   line.assigned_team.prepress,    "bg-teal-50 text-teal-700 border-teal-200"],
          ["Production", line.assigned_team.production,  "bg-orange-50 text-orange-700 border-orange-200"],
          ["ISM",        line.assigned_team.ism,         "bg-emerald-50 text-emerald-700 border-emerald-200"],
        ].map(([role, people, cls]) => (
          <div key={role} className={`rounded-xl p-2.5 border ${cls}`}>
            <div className="text-[9px] font-mono uppercase tracking-widest opacity-70 flex items-center gap-1">
              <Users className="w-3 h-3" /> {role}
            </div>
            <div className="text-xs font-medium mt-0.5 truncate">
              {people.length ? people.join(", ") : <span className="opacity-50">Unassigned</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Timeline — vertical spine */}
      <div className="relative pl-6 border-l-2 border-zinc-200 space-y-3">
        {STAGES.map((s) => {
          const at = line.timings[s.at];
          const by = line.actors[s.by];
          return (
            <div key={s.key} className="relative" data-testid={`report-stage-${s.key}-${line.boq_id}`}>
              <span className={`absolute -left-[31px] top-1 w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${at ? "bg-emerald-500 border-emerald-500" : "bg-white border-zinc-300"}`}>
                {at ? <CheckCircle2 className="w-3 h-3 text-white" /> : <Circle className="w-2 h-2 text-zinc-300" />}
              </span>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                  <span className={`text-sm font-medium ${at ? "text-zinc-900" : "text-zinc-400"}`}>
                    {s.label}
                  </span>
                  {by && <span className="ml-2 text-[11px] text-zinc-500">by {by}</span>}
                </div>
                <span className={`font-mono text-[11px] ${at ? "text-zinc-600" : "text-zinc-300"}`}>
                  {at ? fmtIST(at) : "— pending —"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Duration strip with SLA colouring (Msg 1063) */}
      <div className="mt-5 pt-4 border-t border-dashed border-zinc-200">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
          <Timer className="w-3 h-3" /> Time spent per phase
          <span className="ml-2 text-[9px] font-normal normal-case text-zinc-400">(green ≤ SLA · red &gt; SLA)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(DURATION_LABELS).map(([k, label]) => {
            const hrs = line.durations_hrs[k];
            const sla = SLA_HRS[k];
            let cls = "bg-zinc-50 text-zinc-400 border-zinc-200";
            if (hrs != null) {
              cls = hrs <= sla
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-rose-50 text-rose-700 border-rose-300";
            }
            return (
              <span key={k}
                    title={`SLA target: ${sla}h`}
                    className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-mono border ${cls}`}>
                {label}: <b>{hrs != null ? `${hrs}h` : "—"}</b>
                {hrs != null && hrs > sla && <AlertTriangle className="w-3 h-3" />}
              </span>
            );
          })}
        </div>
        {line.total_elapsed_hrs != null && (
          <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Total elapsed: <b className="font-mono">{line.total_elapsed_hrs}h</b>
          </div>
        )}
      </div>
    </div>
  );
}

function CurrentStagePill({ stage }) {
  const map = {
    design_in_progress: ["Design in Progress", "bg-blue-100 text-blue-700 border-blue-200"],
    client_approved:    ["Client Approved",    "bg-emerald-100 text-emerald-700 border-emerald-200"],
    pre_press:          ["Pre-Press",          "bg-teal-100 text-teal-700 border-teal-200"],
    in_production:      ["In Production",      "bg-orange-100 text-orange-700 border-orange-200"],
    delivered:          ["Delivered",          "bg-pink-100 text-pink-700 border-pink-200"],
    installed:          ["Installed",          "bg-green-100 text-green-800 border-green-300"],
  };
  const [label, cls] = map[stage] || ["Unknown", "bg-zinc-100 text-zinc-600"];
  return (
    <span className={`inline-flex items-center px-3 h-7 rounded-full text-xs font-medium border ${cls}`}>
      {label}
    </span>
  );
}

export { Kpi };
